'use client'

import { useEffect, useRef, useState } from 'react'
import { Camera, FolderOpenIcon, Pencil, X } from 'lucide-react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  ExportIcon,
  SpeakerIcon,
  TextFormatIcon,
} from '@/app/components/functions/icons/app-icons'
import {
  DEFAULT_SETTINGS,
  FIXED_TEXT_STYLES,
  loadSettings,
  type TextDisplaySettings,
} from '@/app/components/functions/util/text-display-settings'
import {
  HELP_SECTIONS,
  HELP_TITLE,
  type HelpBlock,
  type HelpIconKey,
  type HelpSection,
} from './help-content'

/**
 * Help, as an overlay over whatever the reader was doing (DYS-050).
 *
 * An overlay rather than a page, and that is not a styling preference. The app is
 * one route with a state-driven screen model in app/page.tsx, and the reading
 * screen holds the recognised text in React state alone - which is why leaving it
 * asks "Czy na pewno chcesz wyjść? Twój przetworzony tekst zostanie utracony".
 * A /pomoc route opened from there would either throw that text away or make the
 * reader dismiss a data-loss warning in order to read the help. Neither is an
 * acceptable price for pressing a question mark, so nothing navigates: the screen
 * underneath stays exactly as it was, text and all.
 *
 * Built on the Radix dialog the project already has, which is where the focus
 * trap and Escape-to-close come from. Focus restoration is not - it was measured
 * and it does not happen here - so that one is done explicitly below. The
 * client's brief asked for the text to meet accessibility criteria, and for a
 * dialog most of that is focus behaviour rather than markup.
 */

/** Which component each content icon key resolves to. */
const HELP_ICONS: Record<HelpIconKey, (props: { className?: string }) => React.ReactNode> = {
  camera: ({ className }) => <Camera className={className} aria-hidden="true" />,
  file: ({ className }) => <FolderOpenIcon className={className} aria-hidden="true" />,
  edit: ({ className }) => <Pencil className={className} aria-hidden="true" />,
  customise: ({ className }) => <TextFormatIcon className={className} />,
  speaker: ({ className }) => <SpeakerIcon className={className} />,
  export: ({ className }) => <ExportIcon className={className} />,
}

function HelpIcon({ icon, className }: { icon: HelpIconKey; className: string }) {
  const Render = HELP_ICONS[icon]
  return <Render className={className} />
}

/**
 * The reader's own reading settings, or the defaults until they have chosen any.
 *
 * The brief asked for "domyślne ustawienia dyslexy", and for a first-time reader
 * that is exactly what this returns, because loadSettings falls back to the
 * defaults. Where it departs is for someone who has already set 24px on cream:
 * they chose that because smaller text on white is hard for them, and help is the
 * last place in the app that should argue with that. Same settings object, same
 * fixed letter and word spacing, so Help reads the way their documents read.
 *
 * Read on open rather than on mount: the drawer can change these while the app is
 * running, and a panel that opens showing last session's size would be its own
 * small bug.
 */
function useReadingSettings(open: boolean): TextDisplaySettings {
  const [settings, setSettings] = useState<TextDisplaySettings>(DEFAULT_SETTINGS)

  useEffect(() => {
    if (open) setSettings(loadSettings())
  }, [open])

  return settings
}

/**
 * The height of the panel, in pixels, tracking the part of the screen you can
 * actually see.
 *
 * `92vh` was wrong on a phone and wrong in the way that hides the close button.
 * In Safari on iOS `vh` is the viewport with the browser's toolbars *retracted*,
 * so while they are on screen 92vh is taller than what is visible; the panel is
 * centred with a transform, so the overflow is split top and bottom and the top
 * of it - the header, and the button that closes this - sits above the visible
 * area. The client reported exactly that on an iPhone 16 Pro Max, and in Safari
 * could not find the button at all.
 *
 * `visualViewport` is the only thing that reports what is actually on screen,
 * toolbars and pinch-zoom included, and it is what fires when they slide away.
 * `dvh` would cover the toolbars but not old Safari, and this app supports 15.
 * The same trick is already in text-display-screen as `--vh`; it is done here
 * rather than reused because Help also opens from the start page, where nothing
 * sets that variable.
 */
function useVisibleHeight(open: boolean): number | null {
  const [height, setHeight] = useState<number | null>(null)

  useEffect(() => {
    if (!open) return

    const measure = () => {
      const viewport = window.visualViewport
      setHeight(viewport ? viewport.height : window.innerHeight)
    }

    measure()
    window.visualViewport?.addEventListener('resize', measure)
    window.visualViewport?.addEventListener('scroll', measure)
    window.addEventListener('resize', measure)
    window.addEventListener('orientationchange', measure)

    return () => {
      window.visualViewport?.removeEventListener('resize', measure)
      window.visualViewport?.removeEventListener('scroll', measure)
      window.removeEventListener('resize', measure)
      window.removeEventListener('orientationchange', measure)
    }
  }, [open])

  return height
}

function HelpBlocks({ blocks, keyPrefix }: { blocks: HelpBlock[]; keyPrefix: string }) {
  return (
    <>
      {blocks.map((block, index) => {
        const key = `${keyPrefix}-${index}`

        if (block.kind === 'list') {
          return (
            <ul key={key} className="my-3 list-disc space-y-1 pl-6">
              {block.items.map((item, itemIndex) => (
                <li key={`${key}-${itemIndex}`}>{item}</li>
              ))}
            </ul>
          )
        }

        if (block.kind === 'action') {
          /*
           * The icon, the button's own name, and what it does - in that order,
           * because that is the order the reader meets them on screen. The label
           * is quoted exactly as the app spells it; help that renames a control
           * is worse than no help.
           *
           * The icon is decorative here: its meaning is the label sitting beside
           * it, and a screen reader that announced both would say the same thing
           * twice.
           */
          return (
            <p key={key} className="my-3 flex items-start gap-3">
              <span
                /* bg-current, like the note's border: a fixed tint was invisible
                   on the two dark presets and only looked deliberate on the light
                   ones. Deriving it from the reader's own text colour gives the
                   same faint chip on all nine. */
                className="mt-0.5 flex-shrink-0 rounded-md bg-current/10 p-1.5"
                aria-hidden="true"
              >
                <HelpIcon icon={block.icon} className="size-5" />
              </span>
              <span>
                <strong>{block.label}</strong>
                {' — '}
                {block.text}
              </span>
            </p>
          )
        }

        if (block.kind === 'note') {
          /*
           * Set apart by a rule rather than by a coloured panel: the reader has
           * chosen the background, and a note painted in some other colour would
           * fight whichever pair they picked - including the two dark ones. A
           * border inherits nothing and works on all nine.
           */
          return (
            <p
              key={key}
              className="my-4 border-l-4 border-current/25 pl-4"
            >
              <strong>Ważne:</strong> {block.text}
            </p>
          )
        }

        return (
          <p key={key} className="my-3">
            {block.text}
          </p>
        )
      })}
    </>
  )
}

function HelpSectionView({ section }: { section: HelpSection }) {
  return (
    <section aria-labelledby={`help-${section.id}`} className="mt-8 first:mt-0">
      {/*
        * h3, because the dialog's own title is the h2 above it. The source
        * document numbered its sub-headings inconsistently - level 3 in some
        * sections, level 2 in others, for the same job - and a document whose
        * selling point is accessibility cannot ship with its heading order
        * wrong, so the nesting is structural here rather than per-heading.
        */}
      <h3
        id={`help-${section.id}`}
        className="flex items-center gap-2.5 font-bold"
        style={{ fontSize: '1.15em' }}
      >
        {section.icon && (
          <HelpIcon icon={section.icon} className="size-6 flex-shrink-0" />
        )}
        {section.heading}
      </h3>

      <HelpBlocks blocks={section.blocks} keyPrefix={section.id} />

      {section.subsections?.map((subsection, index) => (
        <div key={`${section.id}-sub-${index}`} className="mt-5">
          <h4 className="font-bold" style={{ fontSize: '1.05em' }}>
            {subsection.heading}
          </h4>
          <HelpBlocks blocks={subsection.blocks} keyPrefix={`${section.id}-sub-${index}`} />
        </div>
      ))}
    </section>
  )
}

export interface HelpOverlayProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function HelpOverlay({ open, onOpenChange }: HelpOverlayProps) {
  const settings = useReadingSettings(open)
  const visibleHeight = useVisibleHeight(open)

  /*
   * Where the keyboard goes back to when Help closes.
   *
   * The dialog primitive is supposed to do this and here it does not: measured
   * with a keyboard, focus after Escape lands on <body>, so a reader who opened
   * Help from the keyboard is returned to the top of the document and has to tab
   * all the way back to where they were. That is a worse outcome on the reading
   * screen than on the start page, because the control they came from is one of
   * five in a toolbar.
   *
   * So it is done here, explicitly, rather than left to the primitive: remember
   * what had focus when the panel opened, and put it back. preventDefault on the
   * close is what stops the primitive from also having an opinion.
   */
  const returnFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (open && document.activeElement instanceof HTMLElement) {
      returnFocusRef.current = document.activeElement
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        * Nearly the whole viewport, and scrolling inside rather than outside.
        * This is twelve sections of running text; a centred card at the dialog's
        * default max-w-lg would make it a letterbox, and on a phone there is no
        * room for a margin worth having.
        */}
      <DialogContent
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          returnFocusRef.current?.focus()
        }}
        /*
          * `[&>button]:hidden` takes out the close button the shared DialogContent
          * draws for itself. It is positioned absolutely at the top right of the
          * panel, so anything that clips the panel's top edge takes the button
          * with it - which is the failure being fixed here. The replacement below
          * sits in the header's own flex row, in normal flow, where it cannot be
          * clipped without the title going too.
          */
        className="flex w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:w-[calc(100vw-4rem)] [&>button]:hidden"
        style={{
          backgroundColor: settings.backgroundColor,
          color: settings.textColor,
          // Falls back to vh only for the first frame, before the measurement
          // lands, and on anything without visualViewport.
          height: visibleHeight ? `${Math.round(visibleHeight * 0.92)}px` : '92vh',
        }}
      >
        {/*
          * The header does not take the reader's colours. It is the frame around
          * the document rather than part of it, and it holds the close button,
          * which has to stay findable whichever of the nine pairs is in use.
          */}
        <div
          className="flex flex-shrink-0 items-start gap-3 border-b border-[#1E3144]/10 bg-[#FBF8FF] px-5 py-4 text-[#1E3144]"
          // The notch and the Dynamic Island sit over the top of the panel on a
          // modern iPhone, and the title and the close button are both up here.
          style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
        >
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-xl font-bold">{HELP_TITLE}</DialogTitle>
            <DialogDescription className="mt-1 text-sm text-[rgba(78,85,98,1)]">
              Jak korzystać z Dyslexy — krok po kroku.
            </DialogDescription>
          </div>

          {/* 44px, which is the smallest target iOS treats as reliably tappable. */}
          <DialogClose
            aria-label="Zamknij Pomoc"
            className="-mr-2 -mt-1 flex size-11 flex-shrink-0 items-center justify-center rounded-full text-[#1E3144] transition-colors hover:bg-[#1E3144]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2B0448]"
          >
            <X className="size-6" />
          </DialogClose>
        </div>

        <div
          className="min-h-0 flex-1 overflow-y-auto px-5 py-6"
          style={{
            fontFamily: settings.fontFamily,
            fontSize: `${settings.fontSize}px`,
            lineHeight: settings.lineSpacing / 100,
            letterSpacing: FIXED_TEXT_STYLES.letterSpacing,
            wordSpacing: FIXED_TEXT_STYLES.wordSpacing,
            textAlign: FIXED_TEXT_STYLES.textAlign,
          }}
        >
          {HELP_SECTIONS.map((section) => (
            <HelpSectionView key={section.id} section={section} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
